import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  PROJECT_HEALTH_EXCEL_DOWNLOAD_API_URL,
  PROJECT_REVIEW_HEALTH_API_URL,
  UPDATE_PROJECT_REVIEW_STATUS_URL,
  DELETE_PROJECT_REVIEW_URL,
} from '../../config/constants'
import { requestNotificationsRefresh } from '../../services/notificationsApi'
import { PillPagination } from '../../components/PillPagination'
import { CreateProjectReviewModal } from './CreateProjectReviewModal'
import ProjectReviewFilter from '../../components/ProjectReviewFilter'
import { Button, message, Popconfirm } from 'antd'
import DataState from '../../components/DataState/DataState'
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons'
import { getAuthHeader } from '../../config/auth'
import { apiFetch } from '../../utils/apiClient'
import {
  canEditReview,
  canViewReview,
  getCurrentUserRole,
  hasPermission,
  Permission,
  Role,
} from '../../config/rbac'
import "../../styles/trackon.css"

const DELETE_ALLOWED_ROLES = [Role.ADMIN, Role.PMO, Role.ACCOUNT_MANAGER]








// ─── Helpers ─────────────────────────────────────────────────────────────────

const RAG_TEXT = { G: 'On Track', A: 'Needs Attention', R: 'At Risk', B: 'Not Updated', N: 'Not Updated' }
const RAG_COLOR = {
  G: '#16a34a',
  A: '#f59e0b',
  R: '#ef4444',
  B: '#111827',
  N: '#000000',
}
const RAG_BG = {
  G: '#dcfce7',
  A: '#fef3c7',
  R: '#fee2e2',
  B: '#e5e7eb',
  N: '#f3f4f6',
}

function RagBadge({ rag, size = 'sm' }) {
  const isLarge = size === 'lg'
  const dotSize = isLarge ? 12 : 10
  const pillPadding = isLarge ? '4px 10px' : '2px 6px'

  return (
    <span
      title={RAG_TEXT[rag]}
      aria-label={RAG_TEXT[rag]}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: isLarge ? 28 : 18,
        height: isLarge ? 28 : 18,
        borderRadius: "50%",
        background: RAG_BG[rag],
        border: `1px solid ${RAG_COLOR[rag]}33`,
      }}
    >
      <span
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          background: RAG_COLOR[rag],
          boxShadow: `0 0 0 2px ${RAG_COLOR[rag]}22`,
        }}
      />
    </span>
  )
}

function initials(name) {
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function phaseBadge(phase) {
  const m = {
    Initiation: 'bg-slate-100 text-slate-600',
    Planning: 'bg-violet-50 text-violet-700',
    Execution: 'bg-blue-50 text-blue-700',
    Monitoring: 'bg-amber-50 text-amber-700',
    Closure: 'bg-emerald-50 text-emerald-700',
  }
  return m[phase]
}


function SupportFlag({ value }) {
  if (!value) return <span style={{ color: '#9ca3af' }}>—</span>
  if (value === 'B') return <span style={{ color: '#9ca3af' }}>—</span>

  const normalizedValue = String(value).trim().toUpperCase()

  let flagColor;

  if (normalizedValue === 'YES' || normalizedValue === 'Y' || normalizedValue === 'TRUE') {
    flagColor = RAG_COLOR[normalizedValue]
  } else if (normalizedValue === 'NO' || normalizedValue === 'N' || normalizedValue === 'FALSE') {
    flagColor = RAG_COLOR[normalizedValue]
  } else {
    flagColor = RAG_COLOR[normalizedValue]
  }

  return (
    <span title={value} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="18" height="18" viewBox="0 0 20 20" fill={flagColor} stroke={flagColor} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 2v16M5 2l10 4-10 4" />
      </svg>
    </span>
  )
}

function EbitdaValue({ value, plannedEbitda }) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return <span style={{ color: '#9ca3af' }}>—</span>
  }

  const actualEbitda = Number(String(value).trim().replace(/%$/, ''))

  if (Number.isNaN(actualEbitda)) {
    return <span style={{ color: '#9ca3af' }}>—</span>
  }

  const plannedValue =
    plannedEbitda !== null &&
      plannedEbitda !== undefined &&
      String(plannedEbitda).trim() !== ''
      ? Number(String(plannedEbitda).trim().replace(/%$/, ''))
      : null

  const isRed =
    plannedValue !== null
      ? actualEbitda < plannedValue // Planned EBITDA exists
      : actualEbitda < 20 // Planned EBITDA is null

  return (
    <span
      title={`Actual: ${actualEbitda}%${plannedValue !== null ? ` | Planned: ${plannedValue}%` : ''
        }`}
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: isRed ? '#ef4444' : '#14532d',
      }}
    >
      {actualEbitda}
    </span>
  )
}

const FILTER_TO_RAG_STATUS = {
  // All should not send pmoRating so backend returns the full dataset.
  ALL: undefined,
  AT_RISK: '2',
  NEEDS_ATTENTION: '3',
  ON_TRACK: '4',
}

const SORT_COLUMN_TO_API_FIELD = {
  department: 'portfolio',
  id: 'projectId',
  name: 'projectName',
  contractCategory: 'projectType',
  pm: 'pmName',
  pmRating: 'pmRating',
  dm: 'dmName',
  scope: 'scope',
  schedule: 'schedule',
  quality: 'quality',
  risk: 'riskIssue',
  resources: 'resource',
  budget: 'invoiceStatus',
  supportFromWm: 'supportFromWm',
  supportFromUst: 'supportFromUst',
}

const normalizeRag = (value) => {
  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'G') return 'G'
  if (normalized === 'A') return 'A'
  if (normalized === 'R') return 'R'
  if (normalized === 'B') return 'B'
  if (normalized === '4') return 'G'
  if (normalized === '3') return 'A'
  if (normalized === '2') return 'R'
  if (normalized === '1') return 'B'
  return 'B'
}

const mapRatingToHealth = (rating) => {
  if (rating === 1) return 'B'
  if (rating === 2) return 'R'
  if (rating === 3) return 'A'
  if (rating === 4) return 'G'
  return 'B'
}

// const mapPmRatingToStatus = (pmRating: number | null | undefined): RAG => {
//   if (pmRating === 1) return 'B'
//   if (pmRating === 2) return 'R'
//   if (pmRating === 3) return 'A'
//   if (pmRating === 4) return 'G'
//   return 'N'
// }

const mapApiProjectToProject = (item) => ({
  id: String(item.projectId || '-'),
  name: String(item.projectName || '-'),
  contractCategory: String(item.projectType || '-'),
  pm: String(item.pmName || '-'),
  pmoRating: item.pmoRating,
  pmRating: item.pmRating,
  pmRatingStatus: mapRatingToHealth(item.pmRating),
  dm: String(item.dmName || '-'),
  sponsor: String(item.dmName || item.pmName || '-'),
  department: String(item.portfolio || '-'),
  phase: 'Execution',
  health: mapRatingToHealth(item.pmoRating),
  schedule: normalizeRag(item.schedule),
  budget: normalizeRag(item.invoiceStatus),
  scope: normalizeRag(item.scope),
  risk: normalizeRag(item.riskIssue),
  quality: normalizeRag(item.quality),
  resources: normalizeRag(item.resource),
  actualEbitda: item?.actualEbitda ?? null,
  plannedEbitda: item?.plannedEbitda ?? null,
  reviewDate: item.reviewDate,
  hasWar: true,
  budget_spent: 0,
  budget_total: 0,
  completion: 0,
  portfolio: String(item.portfolio || ''),
  totalContractValue: item.totalContractValue,
  totalMilestones: item.totalMilestones,
  paidMilestones: item.paidMilestones,
  completedPercentage: item.completedPercentage,
  reviewDate: item.reviewDate,
  supportFromWm: item.supportFromWm,
  supportFromUst: item.supportFromUst,
})

// ─── Avatar ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#0078d4', '#107c10', '#ca5010', '#5c2d91', '#008272', '#d13438']
function avatarColor(name) {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function Avatar({ name, size = 32 }) {
  const bg = avatarColor(name)
  return (
    <div
      style={{ width: size, height: size, backgroundColor: bg, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 600, color: '#fff', letterSpacing: '0.02em' }}
    >
      {initials(name)}
    </div>
  )
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function Icon({ d, size = 16, cls = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={cls}>
      <path d={d} />
    </svg>
  )
}

const ICONS = {
  chevronLeft: 'M12 5l-5 5 5 5',
  chevronRight: 'M8 5l5 5-5 5',
  plus: 'M10 4v12M4 10h12',
  star: 'M10 2l2.4 5 5.6.8-4 3.9.9 5.6L10 14.5l-4.9 2.8.9-5.6L2 7.8l5.6-.8z',
  flag: 'M5 2v16M5 2l10 4-10 4',
  info: 'M10 2a8 8 0 100 16A8 8 0 0010 2zM10 9v5M10 7v1',
  eye: 'M1.5 10s3-5.5 8.5-5.5S18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10zM10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  edit: 'M3 14.5V17h2.5L15.8 6.7l-2.5-2.5L3 14.5zM12.6 4.9l2.5 2.5',
}

// ─── Metric Card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, accent, active, dimmed, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? `linear-gradient(180deg, ${accent}14 0%, ${accent}0a 100%)` : 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
        border: active ? `1px solid ${accent}` : '1px solid #dbe3ef',
        borderRadius: 12,
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: active ? `0 10px 28px ${accent}33` : '0 8px 24px rgba(15, 23, 42, 0.05)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.25s ease',
        opacity: dimmed ? 0.5 : 1,
        filter: dimmed ? 'grayscale(0.5)' : 'none',
        transform: active ? 'translateY(-2px)' : 'none',
      }}
    >
      <div style={{ width: 36, height: 3, borderRadius: 99, background: accent, opacity: 0.8 }} />
      <div style={{ fontSize: 11, fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent, letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: '#475467' }}>{sub}</div> : null}
    </div>
  )
}

function MiniBar({ value, max, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: '#f0f2f5', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, (value / max) * 100)}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#8b92a5', width: 32, textAlign: 'right' }}>{Math.round((value / max) * 100)}%</span>
    </div>
  )
}

// ─── Main Dashboard View ─────────────────────────────────────────────────────

function DashboardView({ onSelectProject }) {
  const [projects, setProjects] = useState([])
  const [filter, setFilter] = useState('ALL')
  const [searchText, setSearchText] = useState('')
  const [contractCategory, setContractCategory] = useState(undefined)
  const [customerName, setCustomerName] = useState(undefined)
  const [sortCol, setSortCol] = useState('id')
  const [sortDir, setSortDir] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  // Key of the last health request that finished. Loading whenever the current view's key differs.
  const [loadedKey, setLoadedKey] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [totalRecords, setTotalRecords] = useState(0)
  const [statusModalProject, setStatusModalProject] = useState(null)
  const [statusDraft, setStatusDraft] = useState(null)
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false)
  const [statusUpdateError, setStatusUpdateError] = useState('')
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState('success')
  const [topCardValues, setTopCardValues] = useState({})
  const [summaryCounts, setSummaryCounts] = useState({})
  const currentRole = getCurrentUserRole()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  // Retry re-runs the health fetch effect (it depends on refreshKey).
  const handleRetryLoad = () => setRefreshKey(k => k + 1)
  const requestKey = [filter, sortCol, sortDir, searchText, currentPage, pageSize, contractCategory, customerName, refreshKey].join('|')
  // Derived in the render that changes any request input, so the first render of a new request is already loading.
  const isLoading = loadedKey !== requestKey
  const listStatus = isLoading ? 'loading' : loadError ? 'error' : 'ready'
  const [deletingId, setDeletingId] = useState(null)
  const queryClient = useQueryClient()
  const canDeleteReview = DELETE_ALLOWED_ROLES.includes(currentRole)

  async function handleDeleteProjectReview(project) {
    if (!canDeleteReview) {
      message.error('You do not have permission to delete project reviews')
      return
    }
    setDeletingId(project.id)
    try {
      const authHeader = getAuthHeader()
      const response = await apiFetch(DELETE_PROJECT_REVIEW_URL(project.id), {
        method: 'DELETE',
        headers: {
          ...(authHeader ? { Authorization: authHeader } : {}),
          Accept: 'application/json',
        },
      }, 'Failed to delete project review')
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to delete project review (${response.status})`)
      }
      message.success('Project review deleted successfully')
      // Drop any cached review data (leadership attention, value adds, etc.) for this project
      queryClient.removeQueries({
        predicate: (q) => q.queryKey[0] === 'project-review' && q.queryKey.includes(project.id),
      })
      requestNotificationsRefresh()
      // Step back a page if the last row on this page was removed
      if (projects.length === 1 && currentPage > 1) {
        setCurrentPage(p => p - 1)
      }
      setRefreshKey(k => k + 1)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to delete project review')
    } finally {
      setDeletingId(null)
    }
  }

  useEffect(() => {
    // Stale responses (from a request that was superseded) must not write state.
    let ignore = false
    const fetchProjectHealth = async () => {
      setLoadError('')
      try {
        const requestUrl = new URL(PROJECT_REVIEW_HEALTH_API_URL, globalThis.location.origin)
        requestUrl.searchParams.set('page', String(currentPage - 1))
        requestUrl.searchParams.set('size', String(pageSize))
        requestUrl.searchParams.set('sortBy', SORT_COLUMN_TO_API_FIELD[sortCol] || 'projectId')
        requestUrl.searchParams.set('sortDirection', sortDir === 1 ? 'asc' : 'desc')

        const pmoRating = FILTER_TO_RAG_STATUS[filter]
        if (pmoRating) {
          requestUrl.searchParams.set('pmoRating', pmoRating)
        }

        // Add search parameter if searchText has 3 or more characters
        if (searchText.trim().length >= 3) {
          requestUrl.searchParams.set('searchText', searchText.trim())
        }

        if (contractCategory) {
          requestUrl.searchParams.set('contractCategory', contractCategory)
        }

        if (customerName) {
          requestUrl.searchParams.set('customerName', customerName)
        }

        const authHeader = getAuthHeader()
        const response = await apiFetch(requestUrl.toString(), {
          method: 'GET',
          headers: {
            ...(authHeader ? { Authorization: authHeader } : {}),
            Accept: 'application/json',
          },
        }, 'Failed to fetch project health data')

        if (!response.ok) {
          throw new Error(`Failed to fetch project health data (${response.status})`)
        }

        const payload = (await response.json())
        // Support both new shape (projects/totalRecords) and legacy shape (content/totalElements)
        const content = Array.isArray(payload.projects) ? payload.projects : (Array.isArray(payload.content) ? payload.content : [])
        if (ignore) return
        setProjects(content.map(mapApiProjectToProject))
        setTotalRecords(Number(payload.totalRecords ?? payload.totalElements ?? content.length ?? 0))
        if (payload.topCardValues) {
          setTopCardValues(payload.topCardValues)
          // Capture the full-dataset summary only when no health filter is active,
          // so the KPI card counts stay stable while a card filter is applied.
          if (filter === 'ALL') {
            setSummaryCounts(payload.topCardValues)
          }
        }
      } catch (error) {
        if (ignore) return
        setProjects([])
        setTotalRecords(0)
        setLoadError(error instanceof Error ? error.message : 'Failed to fetch project health data')
      } finally {
        if (!ignore) setLoadedKey(requestKey)
      }
    }

    void fetchProjectHealth()

    return () => {
      ignore = true
    }
  }, [filter, sortCol, sortDir, searchText, currentPage, pageSize, contractCategory, customerName, refreshKey])

  const counts = {
    total: summaryCounts.totalProjects ?? topCardValues.totalProjects ?? totalRecords,
    G: summaryCounts.onTrack ?? topCardValues.onTrack ?? projects.filter(p => p.health === 'G').length,
    A: summaryCounts.needsAttention ?? topCardValues.needsAttention ?? projects.filter(p => p.health === 'A').length,
    R: summaryCounts.atRisk ?? topCardValues.atRisk ?? projects.filter(p => p.health === 'R').length,
  }

  const filtered = projects

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const paginatedRows = filtered
  const pageStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const pageEnd = Math.min(totalRecords, currentPage * pageSize)

  useEffect(() => {
    setCurrentPage(1)
  }, [filter, sortCol, sortDir, searchText, contractCategory, customerName])

  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize])

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  function openStatusModal(project) {
    setStatusModalProject(project)
    setStatusDraft({
      pmoRating: project.pmoRating ?? undefined,
      pmRating: project.pmRating ?? undefined,
      scope: project.scope,
      schedule: project.schedule,
      quality: project.quality,
      riskIssue: project.risk,
      resource: project.resources,
      invoiceStatus: project.budget,
      supportFromWm: project.supportFromWm,
      supportFromUst: project.supportFromUst

    })
    setStatusUpdateError('')
    setStatusUpdateSuccess(false)
  }

  function closeStatusModal() {
    setStatusModalProject(null)
    setStatusDraft(null)
    setStatusUpdateError('')
    setStatusUpdateSuccess(false)
  }

  async function saveStatusUpdates() {
    if (!statusModalProject || !statusDraft) return

    const canEditAnyReview = canEditReview(currentRole)
    const canEditAssignedReview = canEditReview(currentRole, { isAssignedProject: true })

    if (!canEditAnyReview && !canEditAssignedReview) {
      setStatusUpdateError('You do not have permission to update project review status')
      return
    }

    const canEditPmoFields = hasPermission(currentRole, Permission.REVIEW_EDIT)
    setStatusUpdateLoading(true)
    setStatusUpdateError('')
    setStatusUpdateSuccess(false)

    try {
      const payload = {}

      if (canEditPmoFields) {
        payload.pmoRating = statusDraft.pmoRating
        payload.scope = statusDraft.scope
        payload.schedule = statusDraft.schedule
        payload.quality = statusDraft.quality
        payload.riskIssue = statusDraft.riskIssue
        payload.resource = statusDraft.resource
        payload.invoiceStatus = statusDraft.invoiceStatus
        payload.supportFromUst = statusDraft.supportFromUst
        payload.supportFromWm = statusDraft.supportFromWm
      }

      const apiUrl = UPDATE_PROJECT_REVIEW_STATUS_URL(statusModalProject.id)
      const authHeader = getAuthHeader()

      const response = await apiFetch(apiUrl, {
        method: 'PATCH',
        headers: {
          ...(authHeader ? { Authorization: authHeader } : {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }, 'Failed to update project status')

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to update project status (${response.status})`)
      }

      setStatusUpdateSuccess(true)
      setToastMessage('Project status updated successfully!')
      setToastType('success')

      // PROJECT_STATUS_CHANGED — refresh notification surfaces.
      requestNotificationsRefresh()

      // Refresh the projects list
      const requestUrl = new URL(PROJECT_REVIEW_HEALTH_API_URL, globalThis.location.origin)
      requestUrl.searchParams.set('page', String(currentPage - 1))
      requestUrl.searchParams.set('size', String(pageSize))
      requestUrl.searchParams.set('sortBy', SORT_COLUMN_TO_API_FIELD[sortCol] || 'projectId')
      requestUrl.searchParams.set('sortDirection', sortDir === 1 ? 'asc' : 'desc')

      const pmoRating = FILTER_TO_RAG_STATUS[filter]
      if (pmoRating) {
        requestUrl.searchParams.set('pmoRating', pmoRating)
      }

      if (searchText.trim().length >= 3) {
        requestUrl.searchParams.set('searchText', searchText.trim())
      }

      const refreshResponse = await apiFetch(requestUrl.toString(), {
        method: 'GET',
        headers: {
          ...(authHeader ? { Authorization: authHeader } : {}),
          Accept: 'application/json',
        },
      }, 'Failed to fetch project health data')

      if (refreshResponse.ok) {
        const payload = (await refreshResponse.json())
        const content = Array.isArray(payload.projects) ? payload.projects : (Array.isArray(payload.content) ? payload.content : [])
        setProjects(content.map(mapApiProjectToProject))
        setTotalRecords(Number(payload.totalRecords ?? payload.totalElements ?? content.length ?? 0))
        if (payload.topCardValues) setTopCardValues(payload.topCardValues)
      }

      setTimeout(() => closeStatusModal(), 1500)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to update project status'
      setStatusUpdateError(errorMsg)
      setToastMessage(errorMsg)
      setToastType('error')
    } finally {
      setStatusUpdateLoading(false)
      setTimeout(() => {
        setToastMessage('')
      }, 3000)
    }
  }


  const handleProjectHealthExcelDownload = async () => {
    try {
      const authHeader = getAuthHeader()

      message.loading({
        content: "Downloading Project Health Excel...",
        key: "download",
      });

      const response = await apiFetch(
        PROJECT_HEALTH_EXCEL_DOWNLOAD_API_URL,
        {
          method: "GET",
          headers: {
            ...(authHeader ? { Authorization: authHeader } : {}),
          },
        },
        "Download failed"
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Project_Health_${Date.now()}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({
        content: "Project Health Excel downloaded successfully",
        key: "download",
      });
    } catch (error) {
      console.error("Error downloading Project Health Excel:", error);

      message.error({
        content: "Failed to download Project Health Excel",
        key: "download",
      });
    }
  };

  console.log("ebitda,actual values", projects.map(p => ({ id: p.id, ebitda: p.actualEbitda, planned: p.plannedEbitda })))


  function SortTh({ col, children }) {
    const active = sortCol === col
    return (
      <th
        onClick={() => { if (active) setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol(col); setSortDir(1) } }}
        style={{ padding: '11px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#667085', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', letterSpacing: '0.05em', textTransform: 'uppercase', background: '#f8fafc', borderBottom: '1px solid #dbe3ef', }}
      >
        {children} {active && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}
      </th>
    )
  }
  const metricCellStyle = {
    width: 28,
    minWidth: 28,
    maxWidth: 28,
    padding: '4px 2px',
    textAlign: 'center',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        fontFamily: "'Segoe UI Variable Text', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        background: 'radial-gradient(circle at 10% 0%, #eef4ff 0%, #f8fafc 34%, #f5f7fb 100%)',
        border: '1px solid #e5eaf3',
        borderRadius: 14,
        padding: 20,
      }}
    >

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          width: '100%',
        }}
      >
        <div style={{ flex: "0 0 75%", display: listStatus !== 'error' ? undefined : 'none' }}>
          <ProjectReviewFilter
            onFilterChange={(f) => {
              setSearchText(f.search)
              setContractCategory(f.contractCategory)
              setCustomerName(f.customerName)
            }}
          />
        </div>
        <div style={{ display: listStatus !== 'error' ? 'contents' : 'none' }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            type='primary'
            onClick={handleProjectHealthExcelDownload}
            style={{ borderRadius: "4px", width: "auto", backgroundColor: "#0958d9", fontWeight: 450, height: 40 }}
          >
            Download Excel
          </Button>
        </div>
        </div>
      </div>
      <DataState
        status={listStatus}
        errorTitle="Couldn't load project reviews"
        error={loadError}
        onRetry={handleRetryLoad}
        skeletonRows={6}
        skeletonCards={4}
      >




      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <MetricCard
          label="Total Projects"
          value={counts.total}
          accent="#161b2e"
        />
        <MetricCard
          label="On Track"
          value={counts.G}
          accent="rgba(0, 176, 80, 1)"
          active={filter === 'ON_TRACK'}
          dimmed={filter !== 'ALL' && filter !== 'ON_TRACK'}
          onClick={() => { setFilter(prev => (prev === 'ON_TRACK' ? 'ALL' : 'ON_TRACK')); setCurrentPage(1) }}
        />
        <MetricCard
          label="Needs Attention"
          value={counts.A}
          accent="rgba(255, 192, 0,1)"
          active={filter === 'NEEDS_ATTENTION'}
          dimmed={filter !== 'ALL' && filter !== 'NEEDS_ATTENTION'}
          onClick={() => { setFilter(prev => (prev === 'NEEDS_ATTENTION' ? 'ALL' : 'NEEDS_ATTENTION')); setCurrentPage(1) }}
        />
        <MetricCard
          label="At Risk"
          value={counts.R}
          accent="rgba(255, 0, 0, 1)"
          active={filter === 'AT_RISK'}
          dimmed={filter !== 'ALL' && filter !== 'AT_RISK'}
          onClick={() => { setFilter(prev => (prev === 'AT_RISK' ? 'ALL' : 'AT_RISK')); setCurrentPage(1) }}
        />
      </div>


      {/* Table card */}
      <div style={{ background: '#fff', border: '1px solid #dbe3ef', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 24px rgba(15, 23, 42, 0.05)' }}>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {/* ── Row 1: Level-1 headers ── */}
              <tr>
                <th rowSpan={2} onClick={() => { if (sortCol === 'department') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('department'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#eef2f8', color: '#2d3a5e', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>Customer Name {sortCol === 'department' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th rowSpan={2} onClick={() => { if (sortCol === 'id') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('id'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#eef2f8', color: '#2d3a5e', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', width: "180px" }}>Project{sortCol === 'id' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th
                  rowSpan={2}
                  onClick={() => {
                    if (sortCol === 'contractCategory')
                      setSortDir((d) => (d === 1 ? -1 : 1));
                    else {
                      setSortCol('contractCategory');
                      setSortDir(1);
                    }
                  }}
                  style={{
                    padding: '3px 4px',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    background: '#eef2f8',
                    color: '#2d3a5e',
                    border: '1px solid #dbe3ef',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                    whiteSpace: 'normal',
                    lineHeight: '14px',
                  }}
                >
                  Contract
                  <br />
                  Category{' '}
                  {sortCol === 'contractCategory' && (
                    <span style={{ opacity: 0.7 }}>
                      {sortDir === 1 ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th
                  rowSpan={2}
                  style={{
                    padding: '3px 4px',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    background: '#eef2f8',
                    color: '#2d3a5e',
                    border: '1px solid #dbe3ef',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: 150,
                    minWidth: 150,
                    maxWidth: 150,
                  }}
                >
                  PM / DM
                </th>
                <th
                  rowSpan={2}
                  onClick={() => {
                    if (sortCol === 'lastReviewedDate')
                      setSortDir((d) => (d === 1 ? -1 : 1));
                    else {
                      setSortCol('lastReviewedDate');
                      setSortDir(1);
                    }
                  }}
                  style={{
                    padding: '3px 4px',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    background: '#eef2f8',
                    color: '#2d3a5e',
                    border: '1px solid #dbe3ef',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: 90,
                    minWidth: 90,
                    maxWidth: 90,
                    whiteSpace: 'normal',
                    lineHeight: '14px',
                  }}
                >
                  Last Reviewed
                  <br />
                  Date{' '}
                  {sortCol === 'lastReviewedDate' && (
                    <span style={{ opacity: 0.7 }}>
                      {sortDir === 1 ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th colSpan={2} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#dbeafe', color: '#1e40af', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>Rating</th>
                <th colSpan={7} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#dcfce7', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>Project Health</th>
                <th colSpan={2} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#fef3c7', color: '#78350f', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap' }}>Support</th>
                {canDeleteReview && (
                  <th rowSpan={2} style={{ padding: '3px 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#eef2f8', color: '#2d3a5e', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', width: 60 }}></th>
                )}
              </tr>
              {/* ── Row 2: Level-2 sub-headers ── */}
              <tr>
                {/* Overall Rating */}
                <th style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#eff6ff', color: '#1e40af', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>PMO</th>
                <th onClick={() => { if (sortCol === 'pmRating') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('pmRating'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#eff6ff', color: '#1e40af', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>PM {sortCol === 'pmRating' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                {/* Project Health */}
                <th onClick={() => { if (sortCol === 'scope') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('scope'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Scope {sortCol === 'scope' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'schedule') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('schedule'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Schedule {sortCol === 'schedule' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'quality') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('quality'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Quality {sortCol === 'quality' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'risk') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('risk'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Risk{sortCol === 'risk' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'resources') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('resources'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Resource {sortCol === 'resources' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'budget') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('budget'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>Invoice {sortCol === 'budget' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f0fdf4', color: '#14532d', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>EBITDA %</th>
                {/* Support Needed */}
                <th onClick={() => { if (sortCol === 'supportFromWm') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('supportFromWm'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#fffbeb', color: '#78350f', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>WM {sortCol === 'supportFromWm' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
                <th onClick={() => { if (sortCol === 'supportFromUst') setSortDir(d => (d === 1 ? -1 : 1)); else { setSortCol('supportFromUst'); setSortDir(1) } }} style={{ padding: '3px 4px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#fffbeb', color: '#78350f', border: '1px solid #dbe3ef', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', writingMode: 'vertical-rl', transform: "rotate(180deg)" }}>UST {sortCol === 'supportFromUst' && <span style={{ opacity: 0.7 }}>{sortDir === 1 ? '↑' : '↓'}</span>}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={18} style={{ padding: '20px 12px', textAlign: 'center', color: '#667085' }}>
                    Loading project health data...
                  </td>
                </tr>
              ) : null}
              {!isLoading && paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={18} style={{ padding: '20px 12px', textAlign: 'center', color: '#667085' }}>
                    No projects found.
                  </td>
                </tr>
              ) : null}
              {paginatedRows.map((p, i) => (
                <tr
                  key={p.id}
                  onClick={() => onSelectProject(p)}
                  style={{ background: i % 2 === 0 ? '#fff' : '#fbfcfe', borderBottom: '1px solid #eef2f7', cursor: 'pointer', transition: 'background 0.14s ease' }}
                  onMouseEnter={e => (e.currentTarget).style.background = '#eff6ff'}
                  onMouseLeave={e => (e.currentTarget).style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                >
                  <td style={{ padding: '11px 12px', fontSize: 12, color: '#5c6070' }}>{p.department}</td>
                  <td
                    style={{
                      padding: '8px 12px',
                      width: "auto"
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#3c3c3e',
                        marginBottom: 2,
                      }}
                    >
                      {p.name}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color: '#98a2b3',
                        fontFamily: 'JetBrains Mono, monospace',
                      }}
                    >
                      {p.id}
                    </div>
                  </td>
                  <td style={{ padding: '11px 12px', fontSize: 12, color: '#5c6070' }}>
                    {p.contractCategory}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      fontSize: 12,
                      color: '#5c6070',
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      lineHeight: '18px',
                    }}
                  >
                    <div
                      title={p.pm || '-'}
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: 2,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: '#344054' }}>PM:</span>{' '}
                      {p.pm || '-'}
                    </div>

                    <div
                      title={p.dm}
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: '#344054' }}>DM:</span>{' '}
                      {p.dm?.split(',').length > 1
                        ? `${p.dm.split(',')[0].trim()} +${p.dm.split(',').length - 1}`
                        : p.dm || '-'}
                    </div>
                  </td>
                  <td style={{ padding: '11px 12px', fontSize: 12, color: '#5c6070' }}>
                    {p.reviewDate ? p.reviewDate : '-'}
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={mapRatingToHealth(p.pmoRating) || 'N'} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={mapRatingToHealth(p.pmRating) || 'N'} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.scope} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.schedule} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.quality} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.risk} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.resources} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <RagBadge rag={p.budget} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <EbitdaValue value={p?.actualEbitda} plannedEbitda={p?.plannedEbitda} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <SupportFlag value={mapRatingToHealth(p.supportFromWm)} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <SupportFlag value={mapRatingToHealth(p.supportFromUst)} />
                  </td>
                  {canDeleteReview && (
                  <td style={{ padding: '4px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <Popconfirm
                    overlayClassName='trackon-root'
                      title="Delete project review"
                      description={`Are you sure you want to delete review for ${p.name}?`}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={() => handleDeleteProjectReview(p)}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        title="Delete"
                        aria-label="Delete"
                        loading={deletingId === p.id}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </td>
                  )}
                  {/* <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        title="View"
                        aria-label="View"
                        onClick={e => {
                          e.stopPropagation()
                          onSelectProject(p)
                        }}
                        style={{ border: 'none', background: 'transparent', color: '#0078d4', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' }}
                      >
                        <Icon d={ICONS.eye} size={16} />
                      </button>
                      {(loggedInRole === "Admin" ||
                        loggedInRole === "PMO" || loggedInRole === "Account Manager") && (
                          <button
                            type="button"
                            title="Update Status"
                            aria-label="Update Status"
                            onClick={(e) => {
                              e.stopPropagation();
                              openStatusModal(p);
                            }}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#ca5010",
                              cursor: "pointer",
                              padding: 0,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <EditOutlinedIcon style={{fontSize:"16px"}}/>
                          </button>
                        )}
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '11px 20px', borderTop: '1px solid #eaf0f6', background: '#fcfdff' }}>
          <PillPagination
            currentPage={currentPage}
            pageSize={pageSize}
            total={totalRecords}
            itemLabel="projects"
            onPageChange={setCurrentPage}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <label style={{ fontSize: 12, color: '#475467' }}>Rows per page</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ border: '1px solid #d0d7e2', background: '#fff', color: '#344054', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
            >
              {[3, 10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      </DataState>

      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            background: toastType === 'success' ? '#10b981' : '#ef4444',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            zIndex: 2000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          {toastMessage}
        </div>
      )}

      {statusModalProject && statusDraft && (
        <div
          onClick={closeStatusModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(760px, 100%)',
              background: '#fff',
              border: '1px solid #dbe3ef',
              borderRadius: 14,
              boxShadow: '0 28px 74px rgba(2, 6, 23, 0.22)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e8eef5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#161b2e' }}>Update Project Status</div>
                <div style={{ fontSize: 12, color: '#667085', marginTop: 2 }}>{statusModalProject.id} · {statusModalProject.name}</div>
              </div>
              <button type="button" onClick={closeStatusModal} disabled={statusUpdateLoading} style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, color: '#64748b', cursor: statusUpdateLoading ? 'not-allowed' : 'pointer', opacity: statusUpdateLoading ? 0.5 : 1 }}>×</button>
            </div>

            {statusUpdateError && (
              <div style={{ margin: '10px 18px 0', padding: '10px 12px', border: '1px solid #ffccc7', background: '#fff1f0', borderRadius: 8, color: '#a4262c', fontSize: 12 }}>
                {statusUpdateError}
              </div>
            )}

            <div style={{ padding: 18, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: 12 }}>
              {(() => {
                const canUpdateAllFields = canEditReview(currentRole)
                const canUpdateAssignedFields = canEditReview(currentRole, { isAssignedProject: true })
                const fields = [
                  {
                    key: 'pmoRating',
                    label: 'PMO Rating',
                    show: canUpdateAllFields
                  },
                  {
                    key: 'pmRating',
                    label: 'PM Rating',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'scope',
                    label: 'Scope',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'schedule',
                    label: 'Schedule',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'quality',
                    label: 'Quality',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'riskIssue',
                    label: 'Risk/Issue',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'resource',
                    label: 'Resource',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'invoiceStatus',
                    label: 'Invoice Status',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'supportFromWm',
                    label: 'Support from WM',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  },
                  {
                    key: 'supportFromUst',
                    label: 'Support from UST',
                    show: canUpdateAllFields || canUpdateAssignedFields
                  }
                ]

                return fields
                  .filter(f => f.show)
                  .map(({ key, label }) => {
                    const isRatingField = key === 'pmoRating' || key === 'pmRating' || key === 'supportFromWm' || key === 'supportFromUst'
                    let value = statusDraft[key]

                    // For rating fields, convert number to RAG for display
                    let displayRag = 'N'
                    if (isRatingField && typeof value === 'number') {
                      displayRag = mapRatingToHealth(value)
                    } else if (!isRatingField && typeof value === 'string') {
                      displayRag = (value || 'N')
                    }

                    return (
                      <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#475467', fontWeight: 600 }}>{label}</span>
                        <select
                          disabled={statusUpdateLoading}
                          value={isRatingField ? (value || '') : displayRag}
                          onChange={e => {
                            if (isRatingField) {
                              setStatusDraft(prev => (prev ? { ...prev, [key]: e.target.value ? Number(e.target.value) : undefined } : prev))
                            } else {
                              setStatusDraft(prev => (prev ? { ...prev, [key]: e.target.value } : prev))
                            }
                          }}
                          style={{
                            border: `1px solid ${RAG_COLOR[displayRag]}55`,
                            borderRadius: 999,
                            padding: '7px 10px',
                            fontSize: 13,
                            fontWeight: 600,
                            color: RAG_COLOR[displayRag],
                            outline: 'none',
                            background: RAG_BG[displayRag],
                            cursor: statusUpdateLoading ? 'not-allowed' : 'pointer',
                            opacity: statusUpdateLoading ? 0.6 : 1,
                          }}
                        >
                          {isRatingField ? (
                            <><option value="1">Black (Not Updated)</option>
                              <option value="4">Green (On Track)</option>
                              <option value="3">Amber (Needs Attention)</option>
                              <option value="2">Red (At Risk)</option>

                            </>
                          ) : (
                            <>
                              <option value="G">Green</option>
                              <option value="A">Amber</option>
                              <option value="R">Red</option>
                              <option value="B">Not Updated</option>
                            </>
                          )}
                        </select>
                      </label>
                    )
                  })
              })()}
            </div>

            <div style={{ padding: '12px 18px 16px', display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #eef2f7' }}>
              <button
                type="button"
                onClick={closeStatusModal}
                disabled={statusUpdateLoading}
                style={{ border: '1px solid #d0d7e2', background: '#fff', color: '#344054', borderRadius: 6, padding: '7px 14px', fontWeight: 600, cursor: statusUpdateLoading ? 'not-allowed' : 'pointer', opacity: statusUpdateLoading ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveStatusUpdates}
                disabled={statusUpdateLoading}
                style={{ border: '1px solid #0078d4', background: '#0078d4', color: '#fff', borderRadius: 6, padding: '7px 14px', fontWeight: 600, cursor: statusUpdateLoading ? 'not-allowed' : 'pointer', opacity: statusUpdateLoading ? 0.6 : 1 }}
              >
                {statusUpdateLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateProjectReviewModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          // Refresh the project list
          setCurrentPage(1)
        }}
      />
    </div>
  )
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function ProjectReviewList() {
  const navigate = useNavigate()
  const currentRole = getCurrentUserRole()

  const handleSelectProject = (project) => {
    if (!canViewReview(currentRole)) {
      message.error('You do not have permission to view project review details')
      return
    }

    navigate('/watchtower/review', { state: { project } })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <DashboardView onSelectProject={handleSelectProject} />
    </div>
  )
}

export default ProjectReviewList
